import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaChevronDown, FaChevronUp, FaTimes, FaCog } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import IconButton from '../common/IconButton';
import useApi from '../../hooks/useApi';
import 'animate.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import CommunitySettings from '../engagement/CommunitySettings';
import CommunityOnboarding from './CommunityOnboarding';
import CommunityIdentity, { CommunityAvatar } from '../common/CommunityIdentity';

const CommunityCard = React.memo(({ community, onEdit, onDelete, onToggleStatus, onSettings }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  console.log(`Rendering CommunityCard for community: ${community.id}, name: ${community.name}, isExpanded: ${isExpanded}`);

  const toggleExpand = (e) => {
    e.stopPropagation();
    console.log(`Toggling expand for community: ${community.id}, new state: ${!isExpanded}`);
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="community-card animate__animated animate__fadeInUp">
      <div className="community-card-header" onClick={toggleExpand}>
        <CommunityIdentity community={community} size="sm" />
        <div className="d-flex align-items-center gap-2">
          <span
            className={`badge ${
              community.status === 'ACTIVE' ? 'bg-success' :
                community.status === 'EXPIRED' ? 'bg-warning text-dark' : 'bg-danger'
            }`}
          >
            {community.status}
          </span>
          {isExpanded ? (
            <FaChevronUp size={14} className="text-muted" />
          ) : (
            <FaChevronDown size={14} className="text-muted" />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="community-card-body">
          <div className="mb-2">
            <strong className="text-dark">Expiry Date:</strong>{' '}
            <span className="text-muted">{community.expiryDate || 'No expiry date'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Name:</strong>{' '}
            <span className="text-muted">{community.name}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Area:</strong>{' '}
            <span className="text-muted">{community.area}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">City:</strong>{' '}
            <span className="text-muted">{community.city}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">State:</strong>{' '}
            <span className="text-muted">{community.state}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Country:</strong>{' '}
            <span className="text-muted">{community.country}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Created At:</strong>{' '}
            <span className="text-muted">
              {community.createdAt ? new Date(community.createdAt).toLocaleString() : '-'}
            </span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Updated At:</strong>{' '}
            <span className="text-muted">
              {community.updatedAt ? new Date(community.updatedAt).toLocaleString() : '-'}
            </span>
          </div>
          <div className="mt-2 d-flex gap-2">
            <IconButton
              icon={<FaCog />}
              tooltip="Service and privacy settings"
              variant="primary"
              onClick={(e) => { e.stopPropagation(); onSettings(community); }}
              size="sm"
            />
            <IconButton
              icon={<FaEdit />}
              tooltip="Edit"
              variant="warning"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(community);
              }}
              size="sm"
            />
            <IconButton
              icon={<FaTrash />}
              tooltip="Delete"
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                console.log(`CommunityCard - Triggering delete for community: ${community.id}`);
                onDelete(community.id);
              }}
              size="sm"
            />
            <IconButton
              icon={community.status === 'ACTIVE' ? <FaToggleOff /> : <FaToggleOn />}
              tooltip={community.status === 'ACTIVE' ? 'Disable' :
                community.status === 'EXPIRED' ? 'Update or remove the expiry date before enabling' : 'Enable'}
              variant={community.status === 'ACTIVE' ? 'danger' : 'success'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleStatus(community.id, community.status);
              }}
              size="sm"
              disabled={community.status === 'EXPIRED'}
            />
          </div>
        </div>
      )}
    </div>
  );
});

function CommunityManagement() {
  const { get, post, put, delete: deleteRequest } = useApi();
  const [communities, setCommunities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [communitiesPerPage] = useState(10);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [settingsCommunity, setSettingsCommunity] = useState(null);
  const [communityIdToDelete, setCommunityIdToDelete] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    area: '',
    city: '',
    state: '',
    country: '',
    expiryDate: '',
  });

  useEffect(() => {
    fetchCommunities();
  }, []);

  useEffect(() => {
    if (editingCommunity) {
      setFormData({
        name: editingCommunity.name || '',
        area: editingCommunity.area || '',
        city: editingCommunity.city || '',
        state: editingCommunity.state || '',
        country: editingCommunity.country || '',
        expiryDate: editingCommunity.expiryDate || '',
      });
    } else {
      setFormData({
        name: '',
        area: '',
        city: '',
        state: '',
        country: '',
        expiryDate: '',
      });
    }
  }, [editingCommunity]);

  const fetchCommunities = async () => {
    try {
      setIsLoading(true);
      console.log('CommunityManagement - Fetching communities from /api/communities');
      const communitiesArray = await get('/api/communities');
      console.log('CommunityManagement - Fetched communities:', communitiesArray);
      setCommunities(communitiesArray);
      setError('');
    } catch (err) {
      console.error('CommunityManagement - Fetch communities error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      setError(`Failed to load communities: ${err.message}`);
      setCommunities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.area.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.state.trim() !== '' &&
      formData.country.trim() !== ''
    );
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('All fields are required.');
      return;
    }

    setIsAdding(true);
    try {
      console.log('CommunityManagement - Creating community:', formData);
      let response = await post('/api/communities', formData);
      if (iconFile) {
        try {
          const upload = new FormData();
          upload.append('file', iconFile);
          response = await post(`/api/communities/${response.id}/icon`, upload);
        } catch (iconError) {
          toast.warning(`Community was created, but its icon could not be uploaded: ${iconError.message}`);
        }
      }
      console.log('CommunityManagement - Create response:', response);
      setCommunities([...communities, response]);
      setFormData({ name: '', area: '', city: '', state: '', country: '', expiryDate: '' });
      setIconFile(null);
      setIsAddFormVisible(false);
      toast.success('Community created successfully!');
    } catch (err) {
      console.error('CommunityManagement - Create error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      toast.error('Failed to create community: ' + err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!editingCommunity?.id) {
      console.warn('CommunityManagement - No editingCommunity.id, aborting update');
      toast.error('No community selected for update.');
      setEditingCommunity(null);
      return;
    }

    if (!isFormValid()) {
      toast.error('All fields are required.');
      return;
    }

    setIsUpdating(true);
    try {
      console.log('CommunityManagement - Updating community:', editingCommunity.id, formData);
      let response = await put(`/api/communities/${editingCommunity.id}`, formData);
      if (iconFile) {
        try {
          const upload = new FormData();
          upload.append('file', iconFile);
          response = await post(`/api/communities/${editingCommunity.id}/icon`, upload);
        } catch (iconError) {
          toast.warning(`Community details were updated, but the icon could not be uploaded: ${iconError.message}`);
        }
      }
      console.log('CommunityManagement - Update response:', response);
      if (!response || !response.id) {
        throw new Error('Invalid response from server: Missing community data.');
      }
      setCommunities(communities.map(c => (c.id === editingCommunity.id ? { ...c, ...response } : c)));
      toast.success('Community updated successfully!');
      setEditingCommunity(null);
      setFormData({ name: '', area: '', city: '', state: '', country: '', expiryDate: '' });
      setIconFile(null);
    } catch (err) {
      console.error('CommunityManagement - Update error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      toast.error('Failed to update community: ' + err.message);
      setEditingCommunity(null);
      setFormData({ name: '', area: '', city: '', state: '', country: '', expiryDate: '' });
      setIconFile(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeletePrompt = (id) => {
    console.log('CommunityManagement - Prompting delete for community:', id);
    setCommunityIdToDelete(id);
  };

  const confirmDelete = async () => {
    if (!communityIdToDelete) {
      console.warn('CommunityManagement - No communityIdToDelete set, aborting delete');
      setCommunityIdToDelete(null);
      return;
    }
    setIsDeleting(true);
    try {
      console.log('CommunityManagement - Confirming delete for community:', communityIdToDelete);
      await deleteRequest(`/api/communities/${communityIdToDelete}`);
      setCommunities(communities.filter(c => c.id !== communityIdToDelete));
      toast.success('Community deleted successfully!');
      setCommunityIdToDelete(null);
    } catch (err) {
      console.error('CommunityManagement - Delete error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      toast.error('Failed to delete community: ' + err.message);
      setCommunityIdToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    console.log('CommunityManagement - Canceling delete');
    setCommunityIdToDelete(null);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      console.log('CommunityManagement - Toggling status for community:', id);
      await put(`/api/communities/${id}/toggle-status`);
      setCommunities(communities.map(c => 
        c.id === id ? { ...c, status: currentStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' } : c
      ));
      toast.success('Community status toggled successfully!');
    } catch (err) {
      console.error('CommunityManagement - Toggle status error:', err);
      toast.error('Failed to toggle community status: ' + err.message);
    }
  };

  const handleEdit = (community) => {
    console.log('CommunityManagement - Opening edit form for community:', community.id);
    setIsAddFormVisible(false);
    setEditingCommunity(community);
    setIconFile(null);
  };

  const handleCancelForm = () => {
    setIsAddFormVisible(false);
    setEditingCommunity(null);
    setFormData({ name: '', area: '', city: '', state: '', country: '', expiryDate: '' });
    setIconFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRemoveIcon = async () => {
    if (!editingCommunity?.id || !editingCommunity.iconUrl) return;
    try {
      const response = await deleteRequest(`/api/communities/${editingCommunity.id}/icon`);
      setEditingCommunity(response);
      setCommunities(previous => previous.map(community =>
        community.id === response.id ? response : community
      ));
      setIconFile(null);
      toast.success('Community icon removed.');
    } catch (err) {
      toast.error('Failed to remove community icon: ' + err.message);
    }
  };

  // Client-side pagination
  const indexOfLastCommunity = currentPage * communitiesPerPage;
  const indexOfFirstCommunity = indexOfLastCommunity - communitiesPerPage;
  const currentCommunities = communities.slice(indexOfFirstCommunity, indexOfLastCommunity);
  const totalPages = Math.ceil(communities.length / communitiesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      console.log(`CommunityManagement - Changing to page: ${pageNumber}`);
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="container py-4 animate__animated animate__fadeIn">
      <h2 className="mb-4 fs-4 fw-bold text-dark">Community Management</h2>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3 text-dark">Communities</h5>
          {error && <Alert type="danger" message={error} />}
          {isLoading ? (
            <div className="text-center">Loading communities...</div>
          ) : (
            <>
              <div className="mb-3 d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingCommunity(null);
                    setIsAddFormVisible(true);
                  }}
                  disabled={isAddFormVisible || editingCommunity !== null}
                >
                  <FaPlus className="me-2" /> Create Community
                </Button>
              </div>
              {(isAddFormVisible || editingCommunity !== null) && (
                <div className="card shadow mb-4 animate__animated animate__fadeIn">
                  <div className="card-body">
                    <h5 className="card-title">
                      {editingCommunity ? 'Update Community' : 'Add Community'}
                    </h5>
                    <form onSubmit={editingCommunity ? handleUpdateSubmit : handleCreateSubmit}>
                      <div className="mb-3 community-icon-field">
                        <label className="form-label" htmlFor="communityIcon">Community icon (optional)</label>
                        <div className="community-icon-editor">
                          <CommunityAvatar community={editingCommunity} size="lg" />
                          <div className="flex-grow-1">
                            <input
                              id="communityIcon"
                              type="file"
                              className="form-control"
                              accept="image/png,image/jpeg"
                              onChange={event => setIconFile(event.target.files?.[0] || null)}
                            />
                            <small className="text-muted">PNG or JPEG, maximum 2 MB. A square image works best.</small>
                          </div>
                          {editingCommunity?.iconUrl && (
                            <Button type="button" variant="outline-danger" onClick={handleRemoveIcon}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="Expiry date (optional)"
                          name="expiryDate"
                          type="date"
                          value={formData.expiryDate}
                          onChange={handleInputChange}
                        />
                        <small className="text-muted">
                          On this date, the status changes to EXPIRED and community access is blocked. Leave blank for no expiry.
                        </small>
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="Area"
                          name="area"
                          value={formData.area}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="City"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="State"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <FormInput
                          label="Country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          variant="primary"
                          className="w-50"
                          disabled={(editingCommunity ? isUpdating : isAdding) || !isFormValid()}
                        >
                          {editingCommunity ? <FaEdit /> : <FaPlus />}{' '}
                          {editingCommunity ? 'Update' : 'Add'}
                        </Button>
                        <IconButton
                          icon={<FaTimes />}
                          tooltip="Cancel"
                          variant="secondary"
                          onClick={handleCancelForm}
                          className="w-50"
                          disabled={editingCommunity ? isUpdating : isAdding}
                        />
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {communityIdToDelete !== null && (
                <div className="card shadow mb-4 animate__animated animate__fadeIn">
                  <div className="card-body">
                    <h5 className="card-title">Confirm Delete</h5>
                    <p className="text-muted mb-4">
                      Are you sure you want to delete this community? This action cannot be undone.
                    </p>
                    <div className="d-flex gap-2">
                      <Button
                        variant="danger"
                        onClick={confirmDelete}
                        className="w-50"
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                      <IconButton
                        icon={<FaTimes />}
                        tooltip="Cancel"
                        variant="secondary"
                        onClick={cancelDelete}
                        className="w-50"
                        disabled={isDeleting}
                      />
                    </div>
                  </div>
                </div>
              )}
              {communities.length === 0 ? (
                <Alert type="info" message="No communities found." />
              ) : (
                <>
                  <div className="community-grid">
                    {currentCommunities.map(community => (
                      <CommunityCard
                        key={community.id}
                        community={community}
                        onEdit={handleEdit}
                        onDelete={handleDeletePrompt}
                        onToggleStatus={handleToggleStatus}
                        onSettings={setSettingsCommunity}
                      />
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <>
                      <nav className="mt-4">
                        <ul className="pagination justify-content-center flex-wrap">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                              variant="link"
                              onClick={() => paginate(currentPage - 1)}
                              className="page-link"
                              disabled={currentPage === 1}
                            >
                              Previous
                            </Button>
                          </li>
                          {[...Array(Math.min(totalPages, 5)).keys()].map((i) => {
                            const pageNum = i + Math.max(1, currentPage - 2);
                            if (pageNum > totalPages) return null;
                            return (
                              <li
                                key={pageNum}
                                className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                              >
                                <Button
                                  variant="link"
                                  onClick={() => paginate(pageNum)}
                                  className="page-link"
                                >
                                  {pageNum}
                                </Button>
                              </li>
                            );
                          })}
                          <li
                            className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}
                          >
                            <Button
                              variant="link"
                              onClick={() => paginate(currentPage + 1)}
                              className="page-link"
                              disabled={currentPage >= totalPages}
                            >
                              Next
                            </Button>
                          </li>
                        </ul>
                      </nav>
                      <p className="text-center text-muted">
                        Showing {currentCommunities.length} of {communities.length} communities
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      {settingsCommunity && <div className="card shadow-sm border-0"><div className="card-body"><CommunitySettings communityId={settingsCommunity.id} governanceMode onClose={() => setSettingsCommunity(null)} /><hr className="my-4"/><CommunityOnboarding communityId={settingsCommunity.id} community={settingsCommunity} communityName={settingsCommunity.displayName || settingsCommunity.name}/></div></div>}

      <style>
        {`
          .community-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
            transition: transform 0.2s, box-shadow 0.2s;
            background-color: #fff;
            overflow: hidden;
          }
          .community-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          }
          .community-card-header {
            background-color: #f8f9fa;
            padding: 12px 16px;
            border-bottom: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            gap: 10px;
          }
          .community-card-title {
            flex: 1;
            min-width: 0;
          }
          .community-card-title h6 {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .community-identity-icon {
            display: grid;
            overflow: hidden;
            flex: 0 0 42px;
            width: 42px;
            height: 42px;
            place-items: center;
            color: #5965d8;
            border-radius: 12px;
            background: #e9ebff;
          }
          .community-identity-icon.is-large {
            flex-basis: 64px;
            width: 64px;
            height: 64px;
            font-size: 1.35rem;
          }
          .community-identity-icon img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .community-icon-editor {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          @media (max-width: 576px) {
            .community-icon-editor {
              align-items: stretch;
              flex-direction: column;
            }
          }
          .community-card-body {
            padding: 16px;
            background-color: #fff;
          }
          .community-card .badge {
            font-size: 0.75rem;
            padding: 4px 8px;
            border-radius: 12px;
            margin-right: 4px;
            margin-bottom: 4px;
          }
          .community-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
          }
          .community-card {
            flex: 1 1 300px;
            max-width: 300px;
          }
          @media (max-width: 576px) {
            .community-card {
              flex: 1 1 100%;
              max-width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
}

export default CommunityManagement;
